/**
 * Инструменты разбора: вилы, паттерны, волны, гармоники, Фибоначчи и
 * Ганн второго ряда, проекции.
 *
 * Отдельно от `shapes.ts`, потому что у них общее, которого нет у
 * простых линий: много опорных точек, подписи у точек и вывод — цель,
 * соотношение, средняя цена. Подписи — пилюлями, как у зон индикаторов:
 * один язык на всём графике.
 */

import { withAlpha as tint } from './color.js';
import { onCanvasLeft } from './geometry.js';
import type { Drawing, DrawingPoint } from './types.js';
import type { DrawScope, Screen } from './shapes.js';

/** С чего начинается план сделки: строки, которые остаётся заполнить. */
export const TRADE_PLAN_TEMPLATE = 'Вход: \nСтоп: \nЦель: \nПричина: ';

/** Инструменты этого файла — `drawPattern` рисует только их. */
export const PATTERN_KINDS = new Set<string>([
  'level-zone',
  'pitchfork',
  'schiff-pitchfork',
  'triangle-pattern',
  'head-shoulders',
  'double-pattern',
  'elliott-impulse',
  'elliott-correction',
  'xabcd',
  'fib-channel',
  'fib-arcs',
  'gann-fan',
  'gann-box',
  'projection',
  'forecast-path',
  'dca-ladder',
  'alert-zone',
  'alert-line',
  'trade-plan',
]);

const FONT = '600 10.5px Onest, system-ui, sans-serif';

/* ------------------------------------------------------------ помощники */


function segment(ctx: CanvasRenderingContext2D, a: Screen, b: Screen): void {
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
}

function path(ctx: CanvasRenderingContext2D, points: readonly Screen[]): void {
  if (points.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(points[0]!.x, points[0]!.y);
  for (const point of points.slice(1)) ctx.lineTo(point.x, point.y);
  ctx.stroke();
}

function area(ctx: CanvasRenderingContext2D, points: readonly Screen[], color: string, alpha: number): void {
  if (points.length < 3) return;
  ctx.save();
  ctx.setLineDash([]);
  ctx.fillStyle = tint(color, alpha);
  ctx.beginPath();
  ctx.moveTo(points[0]!.x, points[0]!.y);
  for (const point of points.slice(1)) ctx.lineTo(point.x, point.y);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function dashedSegment(ctx: CanvasRenderingContext2D, a: Screen, b: Screen, alpha = 0.7): void {
  ctx.save();
  ctx.setLineDash([4, 4]);
  ctx.globalAlpha *= alpha;
  ctx.lineWidth = 1;
  segment(ctx, a, b);
  ctx.restore();
}

/** Продолжение луча от `from` в направлении `dir` до края холста. */
function toEdge(scope: DrawScope, from: Screen, dir: Screen): Screen {
  const { width, height } = scope;
  let t = Number.POSITIVE_INFINITY;
  if (dir.x > 0) t = Math.min(t, (width - from.x) / dir.x);
  if (dir.x < 0) t = Math.min(t, -from.x / dir.x);
  if (dir.y > 0) t = Math.min(t, (height - from.y) / dir.y);
  if (dir.y < 0) t = Math.min(t, -from.y / dir.y);
  if (!Number.isFinite(t) || t < 0) return from;
  return { x: from.x + dir.x * t, y: from.y + dir.y * t };
}

/**
 * Занятые подписями места одной фигуры.
 *
 * У веера Ганна и канала Фибоначчи лучи сходятся, и подписи ложились
 * друг на друга кашей. Подпись, которая легла бы поверх уже
 * поставленной, пропускается: луч без подписи читается, каша — нет.
 */
let placed: Array<{ left: number; top: number; right: number; bottom: number }> = [];

/** Пилюля-подпись: тёмная подложка, кромка и текст цвета фигуры. */
function pill(ctx: CanvasRenderingContext2D, at: Screen, text: string, color: string, align: 'left' | 'center' | 'right' = 'center'): void {
  ctx.save();
  ctx.setLineDash([]);
  ctx.font = FONT;
  const width = ctx.measureText(text).width + 12;
  const height = 16;
  // У края графика пилюля сдвигается внутрь, а не обрезается.
  const left = onCanvasLeft(ctx, align === 'center' ? at.x - width / 2 : align === 'left' ? at.x : at.x - width, width);
  const top = at.y - height / 2;
  const box = { left, top, right: left + width, bottom: top + height };
  if (placed.some((other) => box.left < other.right && box.right > other.left && box.top < other.bottom && box.bottom > other.top)) {
    ctx.restore();
    return;
  }
  placed.push(box);
  ctx.beginPath();
  ctx.roundRect(left, top, width, height, height / 2);
  ctx.fillStyle = 'rgba(7, 11, 22, 0.88)';
  ctx.fill();
  ctx.strokeStyle = tint(color, 0.75);
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, left + 6, at.y + 0.5);
  ctx.restore();
}

/** Номер или буква у опорной точки — кружком над вершиной или под впадиной. */
function marker(ctx: CanvasRenderingContext2D, at: Screen, text: string, color: string, above: boolean): void {
  ctx.save();
  ctx.setLineDash([]);
  ctx.font = '700 10px Onest, system-ui, sans-serif';
  const y = at.y + (above ? -14 : 14);
  const radius = Math.max(8, ctx.measureText(text).width / 2 + 5);
  ctx.beginPath();
  ctx.arc(at.x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(7, 11, 22, 0.9)';
  ctx.fill();
  ctx.strokeStyle = tint(color, 0.8);
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, at.x, y + 0.5);
  ctx.restore();
}

/** Вершина это или впадина — по соседям: подпись ставится снаружи излома. */
function isPeak(points: readonly Screen[], index: number): boolean {
  const here = points[index]!;
  const prev = points[index - 1];
  const next = points[index + 1];
  const neighbours = [prev, next].filter((point): point is Screen => point !== undefined);
  if (neighbours.length === 0) return true;
  return neighbours.every((point) => here.y <= point.y);
}

function priceText(value: number): string {
  const abs = Math.abs(value);
  const digits = abs >= 1000 ? 1 : abs >= 1 ? 2 : abs >= 0.01 ? 4 : 6;
  return value.toLocaleString('ru-RU', { maximumFractionDigits: digits, minimumFractionDigits: 0 });
}

function percentText(from: number, to: number): string {
  if (from === 0) return '';
  const value = ((to - from) / from) * 100;
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`;
}

function ratio(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString('ru-RU', { maximumFractionDigits: 3 }) : '—';
}

/** Цена прямой через две точки в момент `time`. */
function priceAt(a: DrawingPoint, b: DrawingPoint, time: number): number {
  if (b.time === a.time) return a.price;
  return a.price + ((b.price - a.price) * (time - a.time)) / (b.time - a.time);
}

/* ------------------------------------------------------------ рисование */

export function drawPattern(scope: DrawScope, shape: Drawing, points: readonly Screen[]): void {
  const { ctx } = scope;
  const color = shape.color;
  const raw = shape.points;
  const [a, b, c] = points;
  if (!a) return;
  placed = [];

  switch (shape.kind) {
    case 'level-zone': {
      if (!b) return;
      // Зона тянется от первой свечи вправо до края: уровень продолжает
      // действовать, пока его не отменили.
      const left = Math.min(a.x, b.x);
      const top = Math.min(a.y, b.y);
      const bottom = Math.max(a.y, b.y);
      ctx.save();
      ctx.setLineDash([]);
      ctx.fillStyle = tint(color, 0.14);
      ctx.fillRect(left, top, scope.width - left, bottom - top);
      ctx.restore();
      segment(ctx, { x: left, y: top }, { x: scope.width, y: top });
      segment(ctx, { x: left, y: bottom }, { x: scope.width, y: bottom });
      const hi = Math.max(raw[0]!.price, raw[1]!.price);
      const lo = Math.min(raw[0]!.price, raw[1]!.price);
      pill(ctx, { x: scope.width - 8, y: (top + bottom) / 2 }, `${priceText(lo)} – ${priceText(hi)} · ${percentText(lo, hi).replace('+', '')}`, color, 'right');
      return;
    }

    case 'pitchfork':
    case 'schiff-pitchfork': {
      if (!b || !c) return;
      // У Шиффа ручка сдвинута на середину первого хода: вилы положе и
      // лучше ложатся на затяжной тренд.
      const pivot = shape.kind === 'schiff-pitchfork' ? { x: a.x, y: (a.y + b.y) / 2 } : a;
      const mid = { x: (b.x + c.x) / 2, y: (b.y + c.y) / 2 };
      const dir = { x: mid.x - pivot.x, y: mid.y - pivot.y };
      if (dir.x === 0 && dir.y === 0) return;
      const upper = toEdge(scope, b, dir);
      const lower = toEdge(scope, c, dir);
      area(ctx, [b, upper, lower, c], color, 0.08);
      segment(ctx, pivot, toEdge(scope, pivot, dir));
      segment(ctx, b, upper);
      segment(ctx, c, lower);
      dashedSegment(ctx, b, c);
      if (pivot !== a) dashedSegment(ctx, a, pivot);
      return;
    }

    case 'triangle-pattern': {
      const d = points[3];
      if (!b || !c || !d) return;
      // Две стороны — по чередующимся вершинам: A–C и B–D. Сходятся —
      // рисуются до вершины треугольника; расходятся — до края.
      const apex = intersect(a, c, b, d);
      const end1 = apex && apex.x > Math.max(c.x, d.x) ? apex : toEdge(scope, c, { x: c.x - a.x, y: c.y - a.y });
      const end2 = apex && apex.x > Math.max(c.x, d.x) ? apex : toEdge(scope, d, { x: d.x - b.x, y: d.y - b.y });
      area(ctx, [a, end1, end2, b], color, 0.1);
      segment(ctx, a, end1);
      segment(ctx, b, end2);
      ctx.save();
      ctx.globalAlpha *= 0.55;
      path(ctx, [a, b, c, d]);
      ctx.restore();
      pill(ctx, { x: (a.x + b.x) / 2, y: Math.min(a.y, b.y) - 16 }, 'Треугольник', color);
      return;
    }

    case 'head-shoulders': {
      if (points.length < 7) return;
      path(ctx, points);
      const top = isPeak(points, 3);
      const [p2, p3, p4, p6] = [raw[2]!, raw[3]!, raw[4]!, raw[6]!];
      // Линия шеи — через две впадины, продолжается вправо до края.
      const neckA = points[2]!;
      const neckB = points[4]!;
      segment(ctx, neckA, toEdge(scope, neckA, { x: neckB.x - neckA.x, y: neckB.y - neckA.y }));
      marker(ctx, points[1]!, 'ЛП', color, top);
      marker(ctx, points[3]!, 'Г', color, top);
      marker(ctx, points[5]!, 'ПП', color, top);
      // Цель — высота головы над шеей, отложенная от шеи в точке пробоя.
      const neckAtHead = priceAt(p2, p4, p3.time);
      const height = p3.price - neckAtHead;
      const breakPrice = priceAt(p2, p4, p6.time);
      const target = breakPrice - height;
      const from = scope.screen({ time: p6.time, price: breakPrice });
      const to = scope.screen({ time: p6.time, price: target });
      if (from && to) {
        dashedSegment(ctx, from, to, 0.9);
        pill(ctx, { x: to.x + 6, y: to.y }, `цель ${priceText(target)} · ${percentText(breakPrice, target)}`, color, 'left');
      }
      return;
    }

    case 'double-pattern': {
      if (points.length < 5) return;
      path(ctx, points);
      const top = isPeak(points, 1);
      const trough = raw[2]!;
      const first = raw[1]!;
      const second = raw[3]!;
      const neck = points[2]!;
      segment(ctx, neck, { x: scope.width, y: neck.y });
      marker(ctx, points[1]!, '1', color, top);
      marker(ctx, points[3]!, '2', color, top);
      const height = (first.price + second.price) / 2 - trough.price;
      const target = trough.price - height;
      const end = raw[4]!;
      const from = scope.screen({ time: end.time, price: trough.price });
      const to = scope.screen({ time: end.time, price: target });
      if (from && to) {
        dashedSegment(ctx, from, to, 0.9);
        pill(ctx, { x: to.x + 6, y: to.y }, `${top ? 'Двойная вершина' : 'Двойное дно'} · цель ${priceText(target)}`, color, 'left');
      }
      return;
    }

    case 'elliott-impulse':
    case 'elliott-correction': {
      path(ctx, points);
      const names = shape.kind === 'elliott-impulse' ? ['0', '1', '2', '3', '4', '5'] : ['0', 'A', 'B', 'C'];
      points.forEach((point, index) => {
        if (index === 0) return;
        marker(ctx, point, names[index] ?? String(index), color, isPeak(points, index));
      });
      return;
    }

    case 'xabcd': {
      if (points.length < 5) return;
      const [X, A, B, C, D] = points as [Screen, Screen, Screen, Screen, Screen];
      area(ctx, [X, A, B], color, 0.12);
      area(ctx, [B, C, D], color, 0.12);
      path(ctx, [X, A, B, C, D]);
      dashedSegment(ctx, X, B);
      dashedSegment(ctx, B, D);
      dashedSegment(ctx, X, D);
      const p = raw.map((point) => point.price);
      const move = (i: number, j: number): number => Math.abs(p[j]! - p[i]!);
      const rows: Array<[Screen, Screen, number]> = [
        [X, B, move(1, 2) / move(0, 1)],
        [B, D, move(3, 4) / move(2, 3)],
        [X, D, move(1, 4) / move(0, 1)],
      ];
      for (const [from, to, value] of rows) pill(ctx, { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 }, ratio(value), color);
      pill(ctx, { x: (B.x + C.x) / 2 + 14, y: (B.y + C.y) / 2 }, ratio(move(2, 3) / move(1, 2)), color);
      ['X', 'A', 'B', 'C', 'D'].forEach((name, index) => marker(ctx, points[index]!, name, color, isPeak(points, index)));
      return;
    }

    case 'fib-channel': {
      if (!b || !c) return;
      // Ширина — от линии A–B до третьей точки по вертикали; уровни —
      // доли этой ширины, как у коррекции.
      const baseY = b.x === a.x ? a.y : a.y + ((b.y - a.y) * (c.x - a.x)) / (b.x - a.x);
      const shift = c.y - baseY;
      const dir = { x: b.x - a.x, y: b.y - a.y };
      for (const level of [0, 0.382, 0.5, 0.618, 1, 1.618]) {
        const from = { x: a.x, y: a.y + shift * level };
        const to = toEdge(scope, from, dir);
        ctx.save();
        ctx.globalAlpha *= level === 0 || level === 1 ? 1 : 0.7;
        segment(ctx, from, to);
        ctx.restore();
        pill(ctx, { x: Math.min(to.x, scope.width - 8), y: to.y }, String(level), color, 'right');
      }
      area(ctx, [a, toEdge(scope, a, dir), toEdge(scope, { x: a.x, y: a.y + shift }, dir), { x: a.x, y: a.y + shift }], color, 0.06);
      return;
    }

    case 'fib-arcs': {
      if (!b) return;
      // Дуги вокруг второй точки, на стороне первой: там, куда цена
      // откатывает.
      const radius = Math.hypot(b.x - a.x, b.y - a.y);
      const up = a.y < b.y;
      dashedSegment(ctx, a, b);
      for (const level of [0.382, 0.5, 0.618, 1]) {
        ctx.beginPath();
        ctx.arc(b.x, b.y, radius * level, up ? Math.PI : 0, up ? Math.PI * 2 : Math.PI);
        ctx.stroke();
        pill(ctx, { x: b.x, y: b.y + (up ? -1 : 1) * radius * level }, String(level), color);
      }
      return;
    }

    case 'gann-fan': {
      if (!b) return;
      // Луч 1×1 — через вторую точку; остальные — крутизна в разы
      // больше или меньше: время против цены.
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const fan: Array<[string, number]> = [
        ['1×8', 1 / 8],
        ['1×4', 1 / 4],
        ['1×3', 1 / 3],
        ['1×2', 1 / 2],
        ['1×1', 1],
        ['2×1', 2],
        ['3×1', 3],
        ['4×1', 4],
        ['8×1', 8],
      ];
      for (const [name, k] of fan) {
        const end = toEdge(scope, a, { x: dx, y: dy * k });
        ctx.save();
        ctx.globalAlpha *= k === 1 ? 1 : 0.6;
        ctx.lineWidth = k === 1 ? shape.width + 0.5 : shape.width;
        segment(ctx, a, end);
        ctx.restore();
        pill(ctx, { x: end.x - (dx >= 0 ? 6 : -6), y: end.y + (dy >= 0 ? -8 : 8) }, name, color, dx >= 0 ? 'right' : 'left');
      }
      return;
    }

    case 'gann-box': {
      if (!b) return;
      const left = Math.min(a.x, b.x);
      const right = Math.max(a.x, b.x);
      const top = Math.min(a.y, b.y);
      const bottom = Math.max(a.y, b.y);
      ctx.save();
      ctx.setLineDash([]);
      ctx.fillStyle = tint(color, 0.06);
      ctx.fillRect(left, top, right - left, bottom - top);
      ctx.restore();
      ctx.strokeRect(left, top, right - left, bottom - top);
      ctx.save();
      ctx.globalAlpha *= 0.55;
      ctx.lineWidth = 1;
      for (const level of [0.25, 0.382, 0.5, 0.618, 0.75]) {
        const x = left + (right - left) * level;
        const y = top + (bottom - top) * level;
        segment(ctx, { x, y: top }, { x, y: bottom });
        segment(ctx, { x: left, y }, { x: right, y });
      }
      segment(ctx, { x: left, y: top }, { x: right, y: bottom });
      segment(ctx, { x: left, y: bottom }, { x: right, y: top });
      ctx.restore();
      return;
    }

    case 'projection': {
      if (!b || !c) return;
      // Прошлое движение A→B, отложенное от третьей точки: тот же размах
      // и то же время.
      const [pa, pb, pc] = [raw[0]!, raw[1]!, raw[2]!];
      const target = { time: pc.time + (pb.time - pa.time), price: pc.price + (pb.price - pa.price) };
      const d = scope.screen(target);
      ctx.save();
      ctx.globalAlpha *= 0.5;
      segment(ctx, a, b);
      ctx.restore();
      if (!d) return;
      ctx.save();
      ctx.setLineDash([6, 4]);
      segment(ctx, c, d);
      ctx.restore();
      arrowHead(ctx, c, d);
      area(ctx, [c, { x: d.x, y: c.y }, d], color, 0.1);
      pill(ctx, { x: d.x + 8, y: d.y }, `${priceText(target.price)} · ${percentText(pc.price, target.price)}`, color, 'left');
      return;
    }

    case 'forecast-path': {
      if (!b) return;
      ctx.save();
      ctx.setLineDash([2, 5]);
      ctx.lineWidth = shape.width + 0.6;
      segment(ctx, a, b);
      ctx.restore();
      arrowHead(ctx, a, b);
      ctx.save();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(b.x, b.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = tint(color, 0.25);
      ctx.fill();
      ctx.restore();
      const bars = scope.logical(raw[1]!.time) - scope.logical(raw[0]!.time);
      pill(ctx, { x: b.x + 10, y: b.y }, `${percentText(raw[0]!.price, raw[1]!.price)} · ${Math.round(Math.abs(bars))} свеч.`, color, 'left');
      return;
    }

    case 'alert-zone': {
      if (!b) return;
      const left = Math.min(a.x, b.x);
      const right = Math.max(a.x, b.x);
      const top = Math.min(a.y, b.y);
      const bottom = Math.max(a.y, b.y);
      ctx.save();
      ctx.setLineDash([]);
      ctx.fillStyle = tint(color, 0.12);
      ctx.fillRect(left, top, right - left, bottom - top);
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(left, top, right - left, bottom - top);
      ctx.restore();
      const hi = Math.max(raw[0]!.price, raw[1]!.price);
      const lo = Math.min(raw[0]!.price, raw[1]!.price);
      bell(ctx, { x: right, y: top }, color);
      pill(ctx, { x: left + 4, y: top - 12 }, `Моя зона · ${priceText(lo)} – ${priceText(hi)}`, color, 'left');
      return;
    }

    case 'alert-line': {
      if (!b) return;
      // Линия продолжается вправо: оповещение считает её продолжение.
      const end = toEdge(scope, a, { x: b.x - a.x, y: b.y - a.y });
      ctx.save();
      ctx.setLineDash([7, 4]);
      segment(ctx, a, end);
      ctx.restore();
      bell(ctx, b, color);
      return;
    }

    case 'trade-plan': {
      // Карточка: заголовок и строки «ключ: значение». Стоп — красным,
      // цель — зелёным: план читается раньше, чем прочитан текст.
      const rows = (shape.text ?? '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const at = line.indexOf(':');
          return at > 0 ? { key: line.slice(0, at).trim(), value: line.slice(at + 1).trim() } : { key: '', value: line };
        });
      ctx.save();
      ctx.setLineDash([]);
      ctx.font = FONT;
      const keyWidth = Math.max(0, ...rows.map((row) => ctx.measureText(row.key).width));
      const valueWidth = Math.max(80, ...rows.map((row) => ctx.measureText(row.value || '—').width));
      const width = Math.min(360, keyWidth + valueWidth + 34);
      const height = 30 + rows.length * 18 + 8;
      // Справа от точки и уезжает с графиком: прижатый к краю план отрывался бы от своей свечи.
      const left = a.x + 10;
      const top = a.y - 14;
      ctx.beginPath();
      ctx.roundRect(left, top, width, height, 10);
      ctx.fillStyle = 'rgba(9, 14, 28, 0.94)';
      ctx.fill();
      ctx.strokeStyle = tint(color, 0.6);
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.beginPath();
      ctx.roundRect(left, top, 3, height, [10, 0, 0, 10]);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.font = '700 11px Onest, system-ui, sans-serif';
      ctx.fillStyle = color;
      ctx.textBaseline = 'middle';
      ctx.fillText('План сделки', left + 12, top + 15);
      ctx.font = FONT;
      rows.forEach((row, index) => {
        const y = top + 34 + index * 18;
        const lower = row.key.toLowerCase();
        const tone = lower.startsWith('стоп') ? '#FF5470' : lower.startsWith('цель') ? '#2EE6B0' : '#EAF0F8';
        ctx.fillStyle = '#8FA0B8';
        ctx.fillText(row.key, left + 12, y);
        ctx.fillStyle = tone;
        ctx.fillText(row.value || '—', left + 20 + keyWidth, y);
      });
      ctx.restore();
      // Точка привязки — ножка к свече.
      ctx.save();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(a.x, a.y, 3, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.restore();
      scope.bounds?.set(shape.id, { left: a.x - 4, top, right: left + width, bottom: top + height });
      return;
    }

    case 'dca-ladder': {
      if (!b || !c) return;
      // Входы лесенкой между первой и второй точкой, равными долями;
      // третья точка — стоп.
      const count = 4;
      const [first, last, stop] = [raw[0]!.price, raw[1]!.price, raw[2]!.price];
      const left = Math.min(a.x, b.x, c.x);
      const right = Math.max(a.x, b.x, c.x, left + 90);
      const entries = Array.from({ length: count }, (_, i) => first + ((last - first) * i) / (count - 1));
      const average = entries.reduce((sum, price) => sum + price, 0) / count;
      const yOf = (price: number): number | null => scope.screen({ time: raw[0]!.time, price })?.y ?? null;
      entries.forEach((price, i) => {
        const y = yOf(price);
        if (y === null) return;
        ctx.save();
        ctx.globalAlpha *= 0.8;
        segment(ctx, { x: left, y }, { x: right, y });
        ctx.restore();
        pill(ctx, { x: left - 6, y }, `вход ${i + 1}`, color, 'right');
      });
      const avgY = yOf(average);
      if (avgY !== null) {
        ctx.save();
        ctx.setLineDash([]);
        ctx.lineWidth = shape.width + 1;
        segment(ctx, { x: left, y: avgY }, { x: right, y: avgY });
        ctx.restore();
        pill(ctx, { x: right + 6, y: avgY }, `средняя ${priceText(average)}`, color, 'left');
      }
      const stopY = yOf(stop);
      if (stopY !== null) {
        ctx.save();
        ctx.strokeStyle = '#FF5470';
        ctx.setLineDash([5, 4]);
        segment(ctx, { x: left, y: stopY }, { x: right, y: stopY });
        ctx.restore();
        pill(ctx, { x: right + 6, y: stopY }, `стоп ${priceText(stop)} · ${percentText(average, stop)}`, '#FF5470', 'left');
      }
      return;
    }
  }
}

/** Колокольчик — знак того, что рисунок сторожит сервер. */
function bell(ctx: CanvasRenderingContext2D, at: Screen, color: string): void {
  ctx.save();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.arc(at.x, at.y, 10, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(7, 11, 22, 0.92)';
  ctx.fill();
  ctx.strokeStyle = tint(color, 0.8);
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.moveTo(at.x - 4.5, at.y + 3);
  ctx.lineTo(at.x + 4.5, at.y + 3);
  ctx.moveTo(at.x - 3.5, at.y + 3);
  ctx.bezierCurveTo(at.x - 3.5, at.y - 5, at.x + 3.5, at.y - 5, at.x + 3.5, at.y + 3);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(at.x, at.y + 4.8, 1.3, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.restore();
}

function arrowHead(ctx: CanvasRenderingContext2D, from: Screen, to: Screen): void {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const size = 8;
  ctx.save();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(to.x - size * Math.cos(angle - 0.45), to.y - size * Math.sin(angle - 0.45));
  ctx.lineTo(to.x - size * Math.cos(angle + 0.45), to.y - size * Math.sin(angle + 0.45));
  ctx.closePath();
  ctx.fillStyle = ctx.strokeStyle;
  ctx.fill();
  ctx.restore();
}

/** Пересечение прямых p1–p2 и p3–p4; параллельные — `null`. */
function intersect(p1: Screen, p2: Screen, p3: Screen, p4: Screen): Screen | null {
  const d = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);
  if (Math.abs(d) < 1e-9) return null;
  const t = ((p1.x - p3.x) * (p3.y - p4.y) - (p1.y - p3.y) * (p3.x - p4.x)) / d;
  return { x: p1.x + t * (p2.x - p1.x), y: p1.y + t * (p2.y - p1.y) };
}
